import React from 'react';
import { Box, Chip, Paper, Tooltip, Typography } from '@mui/material';
import { pct } from '../../utils/platformKpi';

export const SampleChip = () => (
  <Tooltip title="Sample data — real figures arrive with the platform KPI API">
    <Chip
      label="Sample"
      size="small"
      sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, bgcolor: 'warning.light', color: 'warning.contrastText' }}
    />
  </Tooltip>
);

export const SectionTitle = ({ title, source, action }) => (
  <Box data-testid="section-title" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, minHeight: 28, flexWrap: 'wrap' }}>
    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: 'secondary.main', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
      {title}
    </Typography>
    {source === 'sample' && <SampleChip />}
    <Box sx={{ flexGrow: 1 }} />
    {action}
  </Box>
);

export const Section = ({ title, source, action, children, sx }) => (
  <Paper sx={{ p: 2, ...sx }}>
    <SectionTitle title={title} source={source} action={action} />
    {children}
  </Paper>
);

export const KpiTile = ({ label, value, sub, tooltip, onClick, testId, color, sample }) => {
  const body = (
    <Box
      data-testid={testId}
      onClick={onClick}
      sx={{
        p: 1.5,
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        height: '100%',
        minWidth: 0,
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { borderColor: 'primary.main' } : undefined,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{label}</Typography>
        {sample && <SampleChip />}
      </Box>
      <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.3, color: color || 'text.primary', overflowWrap: 'anywhere' }}>
        {value ?? '—'}
      </Typography>
      {sub && <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}>{sub}</Typography>}
    </Box>
  );
  return tooltip ? <Tooltip title={tooltip} placement="top">{body}</Tooltip> : body;
};

export const formatBytes = (n) => {
  if (n == null) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

export const formatAed = (n) => (n == null ? '—' : `AED ${Math.round(n).toLocaleString('en-US')}`);

export const formatCount = (n) => (n == null ? '—' : n.toLocaleString('en-US'));

export const fmtRate = (n, d) => {
  const p = pct(n, d);
  return p == null ? '—' : `${p}% · ${n} / ${d}`;
};

export const sumKnown = (rows, key) => {
  const known = rows.filter((r) => r[key] != null);
  return known.length ? known.reduce((s, r) => s + r[key], 0) : null;
};

// Amber is darkened from #FF9830 so white chip text stays readable.
export const HEALTH_COLORS = { red: '#F2495C', amber: '#D97706', green: '#73BF69' };

/** "Premium", "Free" — the "(free)" suffix only when a non-free-named plan is unpaid. */
export const planLabel = (plan) => {
  if (!plan) return '—';
  const name = plan.name || plan.tier || '—';
  if (plan.paid || /^free$/i.test(name)) return name;
  return `${name} (free)`;
};
export const POTENTIAL_LABELS = { high: 'High', medium: 'Medium', low: 'Low' };
